import {ChangeDetectorRef, Component, HostBinding, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {NgClass, NgIf} from '@angular/common';
import {Subscription} from 'rxjs';
import {Company} from '../../../interfaces/company.interface';
import {
  SqlQualification,
  SqlQualificationService,
  SqlState,
  sqlIsRunning,
  sqlTierClass,
  sqlTierLabel
} from '../../../services/sql-qualification.service';

/**
 * SQL score card next to the ICP score (company header). Read-only view of
 * the shared SqlQualificationService state; every action goes through the
 * "Sales Qualify" wizard that lives in the AI Agent Tool Box.
 */
@Component({
  selector: 'app-sql-qualification',
  standalone: true,
  imports: [NgIf, NgClass],
  templateUrl: './sql-qualification.component.html',
  styleUrl: './sql-qualification.component.scss'
})
export class SqlQualificationComponent implements OnInit, OnChanges, OnDestroy {

  @Input() report: any;
  @Input() company: Company;

  state: SqlState = {enabled: null, qualification: null, creditCost: 3, loaded: false, loading: false, starting: false, saving: false};

  /**
   * Hide the host element itself (not just the card) for accounts outside the
   * allowlist, so the flex slot it sits in collapses instead of leaving a gap.
   */
  @HostBinding('style.display') get hostDisplay(): string | null {
    return this.state.enabled === true ? null : 'none';
  }

  private sub: Subscription | null = null;
  private boundCompanyId: number | null = null;

  constructor(
    private sql: SqlQualificationService,
    private cdr: ChangeDetectorRef
  ) {
  }

  ngOnInit(): void {
    this.bind();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['company'] || changes['report']) {
      this.bind();
    }
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  get qualification(): SqlQualification | null {
    return this.state.qualification;
  }

  get isRunning(): boolean {
    return sqlIsRunning(this.qualification);
  }

  get hasScore(): boolean {
    return !!this.qualification && this.qualification.score !== null && !this.isRunning;
  }

  get tierLabel(): string {
    return sqlTierLabel(this.qualification);
  }

  get tierClass(): string {
    return sqlTierClass(this.qualification);
  }

  get subtitle(): string {
    const q = this.qualification;
    if (!q || q.status === 'failed') {
      return q?.status === 'failed' ? 'Failed · retry' : `Qualify · ${this.state.creditCost} credits`;
    }
    if (this.isRunning) {
      if (q.stop_requested_at) {
        return 'Stopping...';
      }
      const current = q.progress?.steps?.find(s => s.status === 'running' || s.status === 'waiting');
      return current ? current.label + '...' : 'Qualifying...';
    }
    if (q.status === 'needs_input') {
      const n = q.pending_questions?.length ?? 0;
      return `Answer ${n} question${n === 1 ? '' : 's'}`;
    }
    return 'Details';
  }

  openWizard(): void {
    if (this.report?.id && this.company?.id) {
      this.sql.requestOpen(Number(this.report.id), Number(this.company.id));
    }
  }

  private bind(): void {
    const companyId = Number(this.company?.id);
    if (!companyId || companyId === this.boundCompanyId) {
      return;
    }
    this.boundCompanyId = companyId;
    this.sub?.unsubscribe();
    this.sub = this.sql.state$(companyId).subscribe(state => {
      this.state = state;
      this.cdr.markForCheck();
    });

    if (this.report?.user_id) {
      this.sql.listen(Number(this.report.user_id));
    }
    if (this.report?.id && !this.sql.snapshot(companyId).loaded) {
      this.sql.load(Number(this.report.id), companyId);
    }
  }
}
